<template>
  <ui-component-modal
    :modalTitle="
      $t('Components.Invoices.ModalCreateCombinedInvoice.Main_Title')
    "
    :isSaving="isSaving"
    :disableSaveButton="selectedReservations.length === 0"
    :onClickCancel="onClickCancel"
    :onClickSave="goToNextStep"
    :showModal="showModal"
    :buttonLabelConfirm="buttonLabelConfirm"
    @closeModal="onClickCancel"
    modalSize="large"
  >
    <template slot="modalTitle"></template>
    <template v-slot:content>
      <div v-if="showStep1">
        <table class="table is-fullwidth is-striped">
          <thead>
            <tr>
              <th></th>
              <th>
                {{
                  $t('Views.Invoices.ReservationsNoInvoice.TableHeader_ResNr')
                }}
              </th>
              <th>
                {{
                  $t('Views.Invoices.ReservationsNoInvoice.TableHeader_Name')
                }}
              </th>
              <th>
                {{
                  $t('Views.Invoices.ReservationsNoInvoice.TableHeader_Company')
                }}
              </th>
              <th>
                {{
                  $t('Views.Invoices.ReservationsNoInvoice.TableHeader_Date')
                }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="reservation in mReservations" :key="reservation.Id">
              <td>
                <label class="checkbox">
                  <input
                    type="checkbox"
                    :value="reservation"
                    v-model="selectedReservations"
                  />
                </label>
              </td>
              <td>#{{ reservation.Id }}</td>
              <td>{{ reservation.Name }}</td>
              <td v-html="reservation.CompanyName"></td>
              <td>
                {{
                  reservation.StartDate
                    | parseIsoDateStringToDate
                    | dateFormat($i18n.locale, 'longDateFormat')
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="showStep2">
        <div class="columns">
          <div class="column">
            <article
              class="media box"
              v-for="company in filteredCompanies"
              :key="company.CompanyId"
            >
              <div class="media-content">
                <div class="content">
                  {{ company.CompanyName }}
                </div>
              </div>
              <div class="media-right">
                <div>
                  <a @click="getCompanyData(company.CompanyId)">
                    <span class="icon">
                      <font-awesome-icon :icon="['fas', 'chevron-right']" />
                    </span>
                    <span>
                      {{
                        $t(
                          'Components.Invoices.ModalCreateCombinedInvoice.Button_Select'
                        )
                      }}</span
                    >
                  </a>
                </div>
              </div>
            </article>
          </div>
          <div class="column">
            <ui-loader v-if="isLoadingCompany" />
            <table
              v-if="!isLoadingCompany"
              class="table is-fullwidth is-striped is-small"
            >
              <tbody>
                <tr>
                  <td>
                    {{
                      $t(
                        'Components.Invoices.ModalCreateCombinedInvoice.Label_Company'
                      )
                    }}
                  </td>
                  <td>
                    <ui-base-input-field
                      v-model="invoiceAddress.Company"
                      :error="companyErrors"
                      :required="true"
                      :showLabel="false"
                      fieldType="text"
                      @input="$v.invoiceAddress.Company.$touch()"
                      @blur="$v.invoiceAddress.Company.$touch()"
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    {{
                      $t(
                        'Components.Invoices.ModalCreateCombinedInvoice.Label_Address'
                      )
                    }}
                  </td>
                  <td>
                    <ui-base-input-field
                      v-model="invoiceAddress.Address"
                      :error="addressErrors"
                      :required="true"
                      :showLabel="false"
                      fieldType="text"
                      @input="$v.invoiceAddress.Address.$touch()"
                      @blur="$v.invoiceAddress.Address.$touch()"
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    {{
                      $t(
                        'Components.Invoices.ModalCreateCombinedInvoice.Label_PostalCode'
                      )
                    }}
                  </td>
                  <td>
                    <ui-base-input-field
                      v-model="invoiceAddress.PostalCode"
                      :error="postalCodeErrors"
                      :required="true"
                      :showLabel="false"
                      fieldType="text"
                      @input="$v.invoiceAddress.PostalCode.$touch()"
                      @blur="$v.invoiceAddress.PostalCode.$touch()"
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    {{
                      $t(
                        'Components.Invoices.ModalCreateCombinedInvoice.Label_City'
                      )
                    }}
                  </td>
                  <td>
                    <ui-base-input-field
                      v-model="invoiceAddress.City"
                      :error="cityErrors"
                      :required="true"
                      :showLabel="false"
                      fieldType="text"
                      @input="$v.invoiceAddress.City.$touch()"
                      @blur="$v.invoiceAddress.City.$touch()"
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    {{
                      $t(
                        'Components.Invoices.ModalCreateCombinedInvoice.Label_Country'
                      )
                    }}
                  </td>
                  <td>
                    <BaseFieldTemplate :labelForInput="false" :required="false">
                      <template v-slot:fields>
                        <div class="select">
                          <select v-model="invoiceAddress.Country">
                            <option
                              v-for="(country, key) in $t('CountryLabels')"
                              :key="key"
                              :selected="
                                key === 'Country' + invoiceAddress.Country
                              "
                              :value="Number(key.split('Country')[1])"
                            >
                              {{ country }}
                            </option>
                          </select>
                        </div>
                      </template>
                    </BaseFieldTemplate>
                  </td>
                </tr>
                <tr>
                  <td>
                    {{
                      $t(
                        'Components.Invoices.ModalCreateCombinedInvoice.Label_SendTo'
                      )
                    }}
                  </td>
                  <td>
                    <ui-base-input-field
                      v-model="invoiceAddress.SendToName"
                      :error="nameErrors"
                      :required="true"
                      :showLabel="false"
                      fieldType="text"
                      @input="$v.invoiceAddress.SendToName.$touch()"
                      @blur="$v.invoiceAddress.SendToName.$touch()"
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    {{
                      $t(
                        'Components.Invoices.ModalCreateCombinedInvoice.Label_Email'
                      )
                    }}
                  </td>
                  <td>
                    <ui-base-input-field
                      v-model="invoiceAddress.EmailTo"
                      :error="emailErrors"
                      :required="true"
                      :showLabel="false"
                      fieldType="text"
                      @input="$v.invoiceAddress.EmailTo.$touch()"
                      @blur="$v.invoiceAddress.EmailTo.$touch()"
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    {{
                      $t(
                        'Components.Invoices.ModalCreateCombinedInvoice.Label_VatNumber'
                      )
                    }}
                  </td>
                  <td>
                    <input
                      type="text"
                      v-model="invoiceAddress.VatNumber"
                      class="input"
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    {{
                      $t(
                        'Components.Invoices.ModalCreateCombinedInvoice.Label_PoNumber'
                      )
                    }}
                  </td>
                  <td>
                    <input
                      type="text"
                      v-model="invoiceAddress.PoNumber"
                      class="input"
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    {{
                      $t(
                        'Components.Invoices.ModalCreateCombinedInvoice.Label_Language'
                      )
                    }}
                  </td>
                  <td>
                    <div class="tabs is-toggle is-small has-text-right">
                      <ul>
                        <li
                          v-for="l in locationState.Languages"
                          :key="l.LanguageId"
                          :class="{
                            'is-active': language === l.Language,
                          }"
                        >
                          <a @click="language = l.Language">
                            <span>{{
                              $t('LanguageLabels.Language' + l.LanguageId)
                            }}</span>
                          </a>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    {{
                      $t(
                        'Components.Invoices.ModalCreateCombinedInvoice.Label_NoTaxOnInvoice'
                      )
                    }}
                  </td>
                  <td>
                    <label class="radio">
                      <input
                        type="radio"
                        name="status"
                        v-model="invoiceAddress.NoTax"
                        :value="true"
                        :checked="invoiceAddress.NoTax === true"
                      />{{ $t('General.Yes') }}</label
                    >
                    <label class="radio">
                      <input
                        type="radio"
                        name="status"
                        v-model="invoiceAddress.NoTax"
                        :value="false"
                        :checked="invoiceAddress.NoTax === false"
                      />{{ $t('General.No') }}</label
                    >
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-if="showStep3">
        <section v-if="invoice" class="section">
          <div class="container has-text-centered">
            <success-animation />

            <div class="title">
              {{
                $t(
                  'Components.Invoices.ModalCreateCombinedInvoice.Message_InvoiceCreated'
                )
              }}
            </div>

            <div>
              <a @click="onClickCancel">
                <span class="icon">
                  <font-awesome-icon :icon="['fas', 'times']" />
                </span>
                <span>{{
                  $t(
                    'Components.Invoices.ModalCreateCombinedInvoice.Button_ClosePopUp'
                  )
                }}</span>
              </a>
              <span>{{
                $t('Components.Invoices.ModalCreateCombinedInvoice.Text_Or')
              }}</span>
              <a @click="goToInvoice">
                <span class="icon">
                  <font-awesome-icon :icon="['fas', 'file-invoice']" />
                </span>
                <span>{{
                  $t(
                    'Components.Invoices.ModalCreateCombinedInvoice.Button_GoToInvoice'
                  )
                }}</span>
              </a>
            </div>
          </div>
        </section>
      </div>
    </template>
  </ui-component-modal>
</template>

<script>
import { mapState } from 'vuex'
import companyProvider from '@/providers/company'
import invoiceProvider from '@/providers/invoice'
import reservationProvider from '@/providers/reservation'
import { cleanSource } from '@/utils/objectHelper'
import { required, minLength, email } from 'vuelidate/lib/validators'
const touchMap = new WeakMap()

const BaseFieldTemplate = () => import('@/components/UI/Form/BaseFieldTemplate')
const SuccessAnimation = () =>
  import('@/components/UI/Components/SuccessAnimation')

export default {
  components: {
    'success-animation': SuccessAnimation,
    BaseFieldTemplate,
  },

  props: {
    locationId: {
      type: Number,
      default: 0,
    },
    reservations: {
      type: Array,
      default: function () {
        return []
      },
    },
    startDate: {
      type: Date,
      default: function () {
        return new Date()
      },
    },
    endDate: {
      type: Date,
      default: function () {
        return new Date()
      },
    },
    searchTerm: {
      type: String,
      default: '',
    },
    showModal: {
      type: Boolean,
      default: false,
    },
    onClickCancel: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      mEndDate: this.endDate,
      invoice: null,
      invoiceAddress: this.setNewInvoiceAddres(),
      isLoadingCompany: false,
      isSaving: false,
      isSearching: false,
      language: 'en',
      meetingtypeId: 0,
      mReservations: this.reservations,
      mSearchTerm: this.searchTerm,
      selectedReservations: [],
      showNorevenue: false,
      showPaid: false,
      showStep1: true,
      showStep2: false,
      showStep3: false,
      mStartDate: this.startDate,
      step: 1,
    }
  },

  /**
   * Form validation rules
   */
  validations() {
    let output = {
      Company: {
        required,
        minLength: minLength(2),
      },
      Address: {
        required,
        minLength: minLength(2),
      },
      PostalCode: {
        required,
        minLength: minLength(2),
      },
      City: {
        required,
        minLength: minLength(2),
      },
      SendToName: {
        required,
        minLength: minLength(2),
      },
      EmailTo: {
        required,
        email,
      },
    }
    return {
      invoiceAddress: output,
    }
  },

  computed: {
    ...mapState('locationStore', ['locationState']),

    buttonLabelConfirm() {
      if (this.step === 1) {
        return this.$t('Form.Control.Next')
      } else if (this.step === 2) {
        return this.$t('Form.Control.Create')
      } else {
        return this.$t('Form.Control.Close')
      }
    },

    filteredCompanies() {
      let companies = []

      for (let i = 0; i < this.selectedReservations.length; i++) {
        let reservation = this.selectedReservations[i]

        let companyIndex = companies.findIndex(
          (c) => c.CompanyId === reservation.CompanyId
        )
        if (companyIndex === -1) {
          companies.push({
            CompanyId: reservation.CompanyId,
            CompanyName: reservation.CompanyName,
          })
        }
      }

      return companies
    },

    nameErrors() {
      const errors = []
      if (!this.$v.invoiceAddress.SendToName.$dirty) return errors
      !this.$v.invoiceAddress.SendToName.required &&
        errors.push(this.$t('Form.InputErrors.Required'))
      !this.$v.invoiceAddress.SendToName.minLength &&
        errors.push(this.$t('Form.InputErrors.minLength', { minLength: 2 }))
      return errors
    },

    emailErrors() {
      const errors = []
      if (!this.$v.invoiceAddress.EmailTo.$dirty) return errors
      if (!this.$v.invoiceAddress.EmailTo.required) {
        errors.push(this.$t('Form.InputErrors.Required'))
      } else if (!this.$v.invoiceAddress.EmailTo.email) {
        errors.push(this.$t('Form.InputErrors.InvalidEmail'))
      }
      return errors
    },

    companyErrors() {
      const errors = []
      if (!this.$v.invoiceAddress.Company.$dirty) return errors
      !this.$v.invoiceAddress.Company.required &&
        errors.push(this.$t('Form.InputErrors.Required'))
      !this.$v.invoiceAddress.Company.minLength &&
        errors.push(this.$t('Form.InputErrors.minLength', { minLength: 2 }))
      return errors
    },

    addressErrors() {
      const errors = []
      if (!this.$v.invoiceAddress.Address.$dirty) return errors
      !this.$v.invoiceAddress.Address.required &&
        errors.push(this.$t('Form.InputErrors.Required'))
      !this.$v.invoiceAddress.Address.minLength &&
        errors.push(this.$t('Form.InputErrors.minLength', { minLength: 2 }))
      return errors
    },

    postalCodeErrors() {
      const errors = []
      if (!this.$v.invoiceAddress.PostalCode.$dirty) return errors
      !this.$v.invoiceAddress.PostalCode.required &&
        errors.push(this.$t('Form.InputErrors.Required'))
      !this.$v.invoiceAddress.PostalCode.minLength &&
        errors.push(this.$t('Form.InputErrors.minLength', { minLength: 2 }))
      return errors
    },

    cityErrors() {
      const errors = []
      if (!this.$v.invoiceAddress.City.$dirty) return errors
      !this.$v.invoiceAddress.City.required &&
        errors.push(this.$t('Form.InputErrors.Required'))
      !this.$v.invoiceAddress.City.minLength &&
        errors.push(this.$t('Form.InputErrors.minLength', { minLength: 2 }))
      return errors
    },
  },

  created() {
    if (this.locationState) {
      this.language = this.locationState.Settings.DefaultLanguage
    }

    this.mReservations = cleanSource(this.reservations);
  },

  methods: {
    notAlreadySelected(reservationId) {
      let index = this.selectedReservations.findIndex((r) => r.Id === reservationId)
      return index === -1
    },

    setNewInvoiceAddres() {
      let address = {
        CompanyId: 0,
        Company: '',
        Address: '',
        PostalCode: '',
        City: '',
        Country: '',
        SendToName: '',
        EmailTo: '',
        VatNumber: '',
        PoNumberRequired: false,
        NoTax: false,
      }

      return address
    },

    getReservationsWithoutInvoice() {
      let self = this
      self.mReservations = []
      self.isSearching = true

      reservationProvider.methods
        .getReservationsWithoutInvoice(
          self.locationId,
          self.mSearchTerm,
          self.$options.filters.dateObjectIsoDateString(self.mStartDate),
          self.$options.filters.dateObjectIsoDateString(self.mEndDate),
          self.meetingtypeId,
          self.showNorevenue,
          self.showPaid
        )
        .then((response) => {
          if (response.status === 200) {
            if (response.data.MorePages === 0) {
              self.pageState.reachedEnd = true
            }

            let mReservations = response.data
            for (let i = 0; i < mReservations.length; i++) {
              let reservation = mReservations[i]
              reservation.InvoiceId = 0
              self.mReservations.push(reservation)
            }
          }
        })
        .finally(() => {
          self.isSearching = false
        })
    },

    searchReservations(searchQuery) {
      let self = this
      self.isSearching = true

      self.mSearchTerm = searchQuery.searchTerm
      self.mStartDate = searchQuery.startDate
      self.mEndDate = searchQuery.endDate

      self.getReservationsWithoutInvoice()
    },

    getCompanyData(companyId) {
      this.isLoadingCompany = true

      companyProvider.methods
        .getCompanyById(companyId, this.locationId)
        .then((response) => {
          if (response.status === 200) {
            let company = response.data
            this.invoiceAddress.CompanyId = companyId
            this.invoiceAddress.Company = company.Name
            this.invoiceAddress.Address = company.DefaultAddress.Address
            this.invoiceAddress.PostalCode = company.DefaultAddress.PostalCode
            this.invoiceAddress.City = company.DefaultAddress.City
            this.invoiceAddress.Country = company.DefaultAddress.Country
            this.invoiceAddress.SendToName = company.DefaultAddress.ContactName
            this.invoiceAddress.EmailTo = company.DefaultAddress.ContactEmail
            this.invoiceAddress.VatNumber = ''
            this.invoiceAddress.PoNumberRequired = company.PoNrRequired
            this.invoiceAddress.NoTax = company.NoTax
          }
        })
        .finally(() => {
          this.isLoadingCompany = false
        })
    },

    goToNextStep() {
      if (this.step === 1) {
        this.step = 2

        this.showStep1 = false
        this.showStep2 = true
        this.showStep3 = false
      } else if (this.step === 2) {
        this.$v.$touch()
        if (this.$v.$invalid) {
          // 'ERROR'
        } else {
          this.step = 3
          this.showStep1 = false
          this.showStep2 = false
          this.showStep3 = true

          this.createInvoice()
        }
      } else if (this.step === 3) {
        this.onClickCancel()
      }
    },

    createInvoice() {
      this.isSaving = true
      let reservationIds = this.selectedReservations.map((r) => r.Id)
      invoiceProvider.methods
        .createCollectiveInvoice(
          this.locationId,
          this.invoiceAddress,
          reservationIds,
          false,
          this.language
        )
        .then((response) => {
          if (response.status === 200) {
            this.invoice = response.data
          }
        })
        .finally(() => {
          this.isSaving = false
        })
    },

    goToInvoice() {
      this.onClickCancel()

      setTimeout(() => {
        this.$router.push({
          name: `invoices-list-detail`,
          params: { invoiceId: this.invoice.Id },
        })
      }, 500)
    },
  },
}
</script>
